import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BarChart3, BookHeart, BookOpen, Compass, Feather, Library, ShieldCheck } from "lucide-react";
import { MemberGate } from "@/components/MemberGate";
import journalIcon from "@/assets/recovery-journal-app-icon.png";

export const Route = createFileRoute("/journal-home")({
  head: () => ({
    meta: [
      { title: "My Journal — Recovery Trauma™" },
      { name: "description", content: "Your private Recovery Trauma™ journal home, with writing, saved reflections, prompts and Journal Insights." },
      { property: "og:title", content: "My Journal — Recovery Trauma™" },
      { property: "og:description", content: "A calm, private home for writing, saved reflections, prompts and Journal Insights." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: JournalHomeRoute,
});

function JournalHomeRoute() {
  return <MemberGate areaName="Journal home"><JournalHome /></MemberGate>;
}

const cards = [
  { to: "/journal", title: "Write a reflection", copy: "Begin with how you feel, choose a prompt or write freely.", icon: Feather, tone: "bg-gold/10 border-gold/30" },
  { to: "/journal", title: "My reflections", copy: "Return to words you saved, whenever you want to.", icon: BookHeart, tone: "bg-rose-500/5 border-rose-500/20" },
  { to: "/journals", title: "Journal library", copy: "Open every journal and all 172+ trauma-informed prompts.", icon: Library, tone: "bg-sage/10 border-sage/30" },
  { to: "/journal", title: "Journal Insights", copy: "Optional patterns from mood, prompt topic and date only—never your writing.", icon: BarChart3, tone: "bg-blue-500/5 border-blue-500/20" },
] as const;

function JournalHome() {
  return (
    <main className="min-h-screen pb-28">
      <header className="border-b border-gold/20 bg-cream/40">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-4 py-6 sm:px-6">
          <img src={journalIcon} width={1024} height={1024} alt="" className="h-16 w-16 rounded-2xl border border-gold/30 object-cover shadow-sm" />
          <div>
            <p className="text-xs font-semibold uppercase text-[var(--gold-deep)]">Recovery Trauma™</p>
            <h1 className="font-display text-3xl text-foreground">My Journal</h1>
            <p className="mt-1 text-sm text-muted-foreground">Feel. Write. Save. Take a gentle next step.</p>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <Link to="/journal" className="flex min-h-[84px] items-center justify-between gap-4 rounded-2xl border border-gold/40 bg-gold/15 p-5 shadow-sm transition active:scale-[0.99]">
          <span className="flex items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-card text-[var(--gold-deep)]"><Feather className="h-5 w-5" aria-hidden /></span>
            <span><span className="block font-display text-xl text-foreground">Write now</span><span className="mt-0.5 block text-sm text-muted-foreground">Open your private Recovery Journal</span></span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0 text-[var(--gold-deep)]" aria-hidden />
        </Link>

        <section className="mt-7" aria-labelledby="journal-spaces-heading">
          <h2 id="journal-spaces-heading" className="font-display text-xl text-foreground">Your journal spaces</h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {cards.map(({ to, title, copy, icon: Icon, tone }) => (
              <Link key={title} to={to} className={`flex min-h-[170px] flex-col rounded-2xl border p-4 shadow-sm transition active:scale-[0.99] ${tone}`}>
                <Icon className="h-6 w-6 text-[var(--gold-deep)]" aria-hidden />
                <span className="mt-4 font-display text-lg leading-tight text-foreground">{title}</span>
                <span className="mt-2 text-xs leading-relaxed text-foreground/70">{copy}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-7 rounded-2xl border border-border/70 bg-card p-5">
          <div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 text-[var(--gold-deep)]" aria-hidden /><div><h2 className="font-display text-lg text-foreground">Private and yours</h2><p className="mt-1 text-sm leading-relaxed text-muted-foreground">Your account, membership and saved writing are the same here and on the website. Journal text is never used for Insights.</p></div></div>
        </section>

        <nav aria-label="More Recovery Trauma areas" className="mt-5 grid grid-cols-2 gap-3">
          <Link to="/my-healing-journey" className="flex min-h-12 items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-medium text-foreground"><Compass className="h-4 w-4 text-[var(--gold-deep)]" /> Practices</Link>
          <Link to="/learn" className="flex min-h-12 items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-medium text-foreground"><BookOpen className="h-4 w-4 text-[var(--gold-deep)]" /> Learn Hub</Link>
        </nav>
      </div>
    </main>
  );
}